import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Modal from "../../../ui/Modal";
import { fetchWithAuth } from "../../../../utils/api";
import Navbar from "../../../ui/Navbar";
import {
  swalConfirm,
  swalError,
  swalSuccess,
} from "../../../../utils/swalAlert";
import Loader from "../../../ui/Loader";
import isEqual from "lodash/isEqual";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const ManageContract = () => {
  const { id } = useParams();
  const instituteId = id;

  const [institute, setInstitute] = useState(null);

  const initialContractData = {
    startDate: null,
    endDate: null,
    currency: "INR",
    STEMGradingPrice: { noChargeQuantity: null, perUsagePrice: null },
    NONSTEMGradingPrice: { noChargeQuantity: null, perUsagePrice: null },
    questionBankGenPrice: { noChargeQuantity: null, perUsagePrice: null },
    imageGenPrice: { noChargeQuantity: null, perUsagePrice: null },
    fixedMonthlyPrice: { type: null, price: null },
  };
  const [newContract, setNewContract] = useState(initialContractData);

  const fieldLabels = {
    startDate: "Start Date",
    endDate: "End Date",
    currency: "Currency",
    STEMGradingPrice: "STEM Grading Price",
    NONSTEMGradingPrice: "NON-STEM Grading Price",
    questionBankGenPrice: "Question Bank Gen Price",
    imageGenPrice: "Image Gen Price",
    fixedMonthlyPrice: "Fixed Monthly Price",
  };

  const [contractDoc, setContractDoc] = useState(null);
  const [fetchedContracts, setFetchedContracts] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const initialEditingContract = {
    _id: null,
    instituteId: null,
    documentURL: null,
    startDate: null,
    endDate: null,
    currency: "INR",
    STEMGradingPrice: { noChargeQuantity: null, perUsagePrice: null },
    NONSTEMGradingPrice: { noChargeQuantity: null, perUsagePrice: null },
    questionBankGenPrice: { noChargeQuantity: null, perUsagePrice: null },
    imageGenPrice: { noChargeQuantity: null, perUsagePrice: null },
    fixedMonthlyPrice: { type: null, price: null },
  };
  const [selectedContract, setSelectedContract] = useState(initialContractData);
  const [editingContract, setEditingContract] = useState(
    initialEditingContract
  );
  const [editingContractDocument, setEditingContractDocument] = useState(null);
  const [error, setError] = useState(null);
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [serverValidationErrors, setServerValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const breadcrumbs = [
    { name: "Home", path: "/superadmin", id: "home" },
    {
      name: "Manage Institutes",
      path: `/superadmin/manage-institutes`,
      isLast: false,
    },
    {
      name: "Manage Contract",
      path: `/superadmin/manage-contract/${id}`,
      isLast: true,
      id: "manage-contract",
    },
  ];

  const fetchInstituteDetails = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/institute/get/${id}`,
        {
          credentials: "include",
        }
      );
      if (!response) return;
      if (!response.ok) throw new Error("Failed to fetch institute details");
      const data = await response.json();
      setInstitute(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [id]);

  useEffect(() => {
    fetchInstituteDetails();
  }, [fetchInstituteDetails]);

  const fetchContractsByInstitute = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/contract/get-all/institute/${instituteId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        const error = data;
        throw new Error(error.message || "Failed to get contract");
      }
      setFetchedContracts(data);
    } catch (error) {
      swalError(error.message || "Failed to get contract");
    } finally {
      setLoading(false);
    }
  }, [instituteId]);

  const handleCreateModalOpen = () => {
    setIsCreateModalOpen(true);
    setNewContract(initialContractData);
    setContractDoc(null);
    setFormValidationErrors({});
    setServerValidationErrors([]);
  };
  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
    setNewContract(initialContractData);
    setContractDoc(null);
    setFormValidationErrors({});
    setServerValidationErrors([]);
  };

  const handleInput = (e) => {
    let { name, value } = e.target;
    setNewContract((prev) => ({
      ...prev,
      [name]: value === "" ? null : value,
    }));
  };

  const handleNestedInput = (section, field, value) => {
    setNewContract((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value === "" ? null : value },
    }));
  };

  const handleFileChange = (e) => {
    setContractDoc(e.target.files[0]);
  };

  const convertSafelyToNumber = (input) => {
    const value = input === "" ? "" : +input;
    return value;
  };

  const validateContractForm = useCallback(() => {
    const newErrors = {};

    let contract = newContract;
    if (isEditModalOpen) contract = editingContract;

    if (!contract.startDate?.trim()) {
      newErrors.startDate = "Start Date is required";
    }

    if (!contract.currency?.trim()) {
      newErrors.currency = "Currency is required";
    }

    // Check that price fields are not negative
    const checkPriceObject = (fieldKey, label) => {
      const field = contract[fieldKey];
      if (field.noChargeQuantity < 0 || field.perUsagePrice < 0) {
        newErrors[fieldKey] = `Values cannot be negative`;
      }
    };

    checkPriceObject("STEMGradingPrice", "STEM Grading Price");
    checkPriceObject("NONSTEMGradingPrice", "Non-STEM Grading Price");
    checkPriceObject("questionBankGenPrice", "Question Bank Generation Price");
    checkPriceObject("imageGenPrice", "Image Generation Price");

    // Fixed Monthly Price: only validate that price is not negative
    const fixed = contract.fixedMonthlyPrice;
    const fixedPrice = fixed.price;

    if (fixedPrice < 0) {
      newErrors.fixedMonthlyPrice = "Price cannot be negative";
    }

    setFormValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [newContract, editingContract, isEditModalOpen]);

  useEffect(() => {
    fetchInstituteDetails();
  }, [fetchInstituteDetails]);

  useEffect(() => {
    fetchContractsByInstitute();
  }, [fetchContractsByInstitute]);

  useEffect(() => {
    validateContractForm();
  }, [newContract, editingContract, validateContractForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validateContractForm()) {
      setIsSubmitting(false);
      return; // Don't proceed if form invalid
    }

    let payloadData = new FormData();
    payloadData.append("instituteId", instituteId);

    for (let key in newContract) {
      const value = newContract[key];

      if (value == null) continue; // Skip null values

      if (typeof value === "object") {
        payloadData.append(key, JSON.stringify(value));
      } else {
        payloadData.append(key, value);
      }
    }

    if (contractDoc) {
      payloadData.append("file", contractDoc);
    }

    try {
      const response = await fetchWithAuth(`${serverUrl}/api/contract/create`, {
        method: "POST",
        credentials: "include",
        body: payloadData,
      });
      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        const error = data;
        if (error.message === "Validation Error") {
          setServerValidationErrors(error.errors || []);
          return;
        }
        throw new Error(error.message || "Failed to create contract");
      }
      swalSuccess(data.message || "Contract created successfully!");
      setIsCreateModalOpen(false);
      setNewContract(initialContractData);
      setContractDoc(null);
      setServerValidationErrors([]);
      fetchContractsByInstitute();
    } catch (error) {
      swalError(error.message || "Failed to create contract. try again");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Modifies the target with valid values from the source (if values are not null/undefined).
   * Deep merge function that works with any level of nesting
   * @param {Object} source - Source object to merge from
   * @param {Object} target - Target object to merge into
   */
  const mergeObjects = (target, source) => {
    if (!source || typeof source !== "object") return target;
    if (!target) return source;

    const result = { ...target };

    Object.keys(source).forEach((key) => {
      // If source value is an object and not null, recursively merge
      if (source[key] !== null && typeof source[key] === "object") {
        // If target doesn't have this key or it's null, initialize it
        if (!result[key] || result[key] === null) {
          result[key] = Array.isArray(source[key]) ? [] : {};
        }
        // Recursively merge nested objects
        result[key] = mergeObjects(result[key], source[key]);
      }
      // Only update if the source value is not null/undefined
      else if (source[key] !== null && source[key] !== undefined) {
        result[key] = source[key];
      }
    });

    return result;
  };

  // Add these handler functions to your component
  const handelOpenEditModal = (contract) => {
    // Set the selected contract for editing
    // Form the complete contract object that includes null values for missing fields
    const completeContractKeepingNulls = mergeObjects(
      initialContractData,
      contract
    );
    setSelectedContract(completeContractKeepingNulls);
    setEditingContract(completeContractKeepingNulls);
    setEditingContractDocument(null);
    setServerValidationErrors([]);
    // Open the edit modal
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingContract(initialEditingContract); // Reset the editing contract state
    setEditingContractDocument(null); // Reset the editing document state
    setSelectedContract(null); // Reset the selected contract state
    setServerValidationErrors([]);
    setIsSubmitting(false);
  };

  const handleEditInput = (e) => {
    let { name, value } = e.target;
    setEditingContract((prev) => ({
      ...prev,
      [name]: value === "" ? null : value,
    }));
  };

  const handleEditNestedInput = (section, field, value) => {
    setEditingContract((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value === "" ? null : value },
    }));
  };

  const handleEditFileChange = (e) => {
    setEditingContractDocument(e.target.files[0]);
  };

  // Include only the fields which have been changed
  const getDiffPayload = (original, updated) => {
    const diff = {};
    for (const key in updated) {
      const newVal = updated[key];
      const oldVal = original[key];
      if (key === "_id") continue; // Skip the _id field
      if (key === "documentURL") continue;
      if (key === "createdAt") continue;
      if (key === "updatedAt") continue;
      if (key === "__v") continue;
      if (key === "instituteId") continue;

      if (typeof newVal === "object" && newVal !== null) {
        const nestedDiff = getDiffPayload(oldVal, newVal);

        if (Object.keys(nestedDiff).length > 0) {
          diff[key] = nestedDiff;
        }
      } else {
        const isNotEqual = !isEqual(oldVal, newVal);
        if (isNotEqual) {
          diff[key] = newVal;
        }
      }
    }

    return diff;
  };

  const isEditModalChanged =
    !isEqual(selectedContract, editingContract) || editingContractDocument;

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const cleanedEditingContract = getDiffPayload(
      selectedContract,
      editingContract
    );

    if (
      Object.keys(cleanedEditingContract).length === 0 &&
      !editingContractDocument
    ) {
      setIsSubmitting(false);
      swalError("No changes to save.");
      return;
    } // No changes to save

    if (!validateContractForm()) {
      setIsSubmitting(false);
      return; // Don't proceed if form invalid
    }

    let payloadData = new FormData();

    for (let key in cleanedEditingContract) {
      const value = cleanedEditingContract[key];

      if (value !== null && typeof value === "object") {
        payloadData.append(key, JSON.stringify(value)); // Stringify the values that are objects
      } else if (value === null) {
        payloadData.append(key, ""); // Send empty string for null values
      } else {
        payloadData.append(key, value);
      }
    }

    if (editingContractDocument) {
      payloadData.append("file", editingContractDocument);
    }

    try {
      const response = await fetchWithAuth(
        `${serverUrl}/api/contract/update/${editingContract._id}`,
        {
          method: "PUT",
          credentials: "include",
          body: payloadData,
        }
      );
      if (!response) return;

      const data = await response.json();
      if (!response.ok) {
        const error = data;
        if (error.message === "Validation Error") {
          setServerValidationErrors(error.errors || []);
          return;
        }
        throw new Error(error.message || "Failed to update contract");
      }
      swalSuccess(data.message || "Contract updated successfully!");
      handleCloseEditModal();
      fetchContractsByInstitute();
    } catch (error) {
      swalError(error.message || "Failed to update contract. try again");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteContract = async (contract) => {
    swalConfirm(
      "Are you sure you want to delete this contract?",
      "You won't be able to revert this!",
      "Yes, Continue!",
      "No, Cancel"
    ).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetchWithAuth(
            `${serverUrl}/api/contract/delete/${contract._id}`,
            {
              method: "DELETE",
              credentials: "include",
            }
          );

          if (!response) return;

          const data = await response.json();

          if (!response.ok) {
            const errorData = data;
            throw new Error(errorData.message || "Failed to delete contract");
          }

          setFetchedContracts(
            fetchedContracts.filter((c) => c._id !== contract._id)
          );
          const message = data.message || "Enrollment deleted successfully";
          swalSuccess(message);
        } catch (error) {
          swalError(error.message || "Failed to delete contract");
        }
      }
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return isNaN(d) ? dateStr : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const contractCards =
    fetchedContracts.length > 0
      ? fetchedContracts.map((contract, index) => (
        <div key={contract._id} className="mc-card">
          {/* Card Header */}
          <div className="mc-card-header">
            <div className="mc-card-header-left">
              <div className="mc-card-id-block">
                <span className="mc-card-label">Contract ID</span>
                <span className="mc-card-id-value">{contract._id}</span>
              </div>
              {contract.documentURL ? (
                <a
                  href={contract.documentURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mc-pdf-link"
                >
                  <i className="fa-solid fa-file-pdf"></i> View PDF
                </a>
              ) : (
                <span className="mc-no-file"><i className="fa-solid fa-file-circle-xmark"></i> No Document</span>
              )}
            </div>
            <div className="mc-card-actions">
              <button
                className="mc-edit-btn"
                onClick={() => handelOpenEditModal(contract)}
              >
                <i className="fa-solid fa-pen-to-square"></i> Edit
              </button>
              <button
                className="mc-delete-btn"
                onClick={() => handleDeleteContract(contract)}
              >
                <i className="fa-solid fa-trash"></i> Delete
              </button>
            </div>
          </div>

          {/* Card Body - Horizontal Grid */}
          <div className="mc-card-body">
            {/* Date & Currency Row */}
            <div className="mc-data-grid">
              <div className="mc-data-cell">
                <span className="mc-cell-icon"><i className="fa-solid fa-calendar-day"></i></span>
                <span className="mc-cell-label">Start Date</span>
                <span className="mc-cell-value mc-date-value">{formatDate(contract.startDate)}</span>
              </div>
              <div className="mc-data-cell">
                <span className="mc-cell-icon"><i className="fa-solid fa-calendar-xmark"></i></span>
                <span className="mc-cell-label">End Date</span>
                <span className="mc-cell-value mc-date-value">{formatDate(contract.endDate)}</span>
              </div>
              <div className="mc-data-cell mc-currency-cell">
                <span className="mc-cell-icon"><i className="fa-solid fa-coins"></i></span>
                <span className="mc-cell-label">Currency</span>
                <span className="mc-cell-value mc-currency-badge">{contract.currency || "N/A"}</span>
              </div>
              <div className="mc-data-cell">
                <span className="mc-cell-icon"><i className="fa-solid fa-tag"></i></span>
                <span className="mc-cell-label">Fixed Price Type</span>
                <span className="mc-cell-value">{contract.fixedMonthlyPrice?.type || "N/A"}</span>
              </div>
              <div className="mc-data-cell mc-highlight-cell">
                <span className="mc-cell-icon"><i className="fa-solid fa-indian-rupee-sign"></i></span>
                <span className="mc-cell-label">Fixed Monthly Price</span>
                <span className="mc-cell-value mc-price-value">{contract.fixedMonthlyPrice?.price ?? "N/A"}</span>
              </div>
            </div>

            {/* Pricing Table */}
            <div className="mc-pricing-table">
              <div className="mc-pricing-header">
                <span className="mc-pricing-col mc-pricing-category">Category</span>
                <span className="mc-pricing-col">Free Qty</span>
                <span className="mc-pricing-col">Per Usage Price</span>
              </div>
              {[
                { key: "STEMGradingPrice", label: "STEM Grading" },
                { key: "NONSTEMGradingPrice", label: "NON-STEM Grading" },
                { key: "questionBankGenPrice", label: "Question Bank Gen" },
                { key: "imageGenPrice", label: "Image Generation" },
              ].map(({ key, label }) => (
                <div key={key} className="mc-pricing-row">
                  <span className="mc-pricing-col mc-pricing-category">
                    {label}
                  </span>
                  <span className="mc-pricing-col mc-pricing-qty">
                    {contract[key]?.noChargeQuantity ?? <span className="mc-na">—</span>}
                  </span>
                  <span className="mc-pricing-col mc-pricing-price">
                    {contract[key]?.perUsagePrice ?? <span className="mc-na">—</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))
      : [];

  return (
    <>
      <Navbar
        pageName={`Manage Institute: ${institute?.name}`}
        activePage="Manage Contract"
        createButtons={[
          { text: "Create Contract", onClick: handleCreateModalOpen },
        ]}
        breadcrumbs={breadcrumbs}
      />

      <div className="mc-page-container">
        <div className="mc-page-header">
          <div className="mc-page-title-block">
            <div className="mc-page-icon"><i className="fa-solid fa-file-contract"></i></div>
            <div>
              <h2 className="mc-page-title">Contracts</h2>
              <p className="mc-page-subtitle">
                {institute?.name ? `Institute: ${institute.name}` : `Institute ID: ${instituteId}`}
              </p>
            </div>
          </div>
          <div className="mc-page-stats">
            <div className="mc-stat-chip">
              <i className="fa-solid fa-file-contract"></i>
              <span>{fetchedContracts.length} Contract{fetchedContracts.length !== 1 ? "s" : ""}</span>
            </div>
          </div>
        </div>

        {loading && <Loader />}
        {error && <div className="error-message">{error}</div>}
        {!loading && !error && (
          <div className="mc-cards-container">
            {contractCards.length > 0 ? (
              contractCards
            ) : (
              <div className="mc-empty-state">
                <i className="fa-solid fa-folder-open"></i>
                <h3>No Contracts Found</h3>
                <p>No contracts have been created for this institute yet.</p>
                <button className="mc-create-empty-btn" onClick={handleCreateModalOpen}>
                  <i className="fa-solid fa-plus"></i> Create First Contract
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal isOpen={isCreateModalOpen} onClose={handleCreateModalClose}>
        <h2>Create New Contract</h2>
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Upload
              Contract Document
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
              required
              placeholder="Upload contract document (.pdf, .jpg, .jpeg, .png)"
            />
            <small className="helper-text">
              Upload a contract file in PDF or image format
            </small>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={newContract.startDate || ""}
                onChange={handleInput}
                required
                placeholder="Select contract start date"
              />
            </div>
            <div className="form-group">
              <label>
                End Date <span className="optional-text">(Optional)</span>
              </label>
              <input
                type="date"
                name="endDate"
                value={newContract.endDate || ""}
                onChange={handleInput}
                placeholder="Select contract end date"
              />
            </div>
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Currency
            </label>
            <input
              type="text"
              name="currency"
              value={newContract.currency || ""}
              onChange={handleInput}
              required
              placeholder="Enter currency (e.g., INR, USD)"
            />
          </div>
          <label>
            Fixed Monthly Price{" "}
            <span className="optional-text">(Optional)</span>
          </label>
          <div className="form-row">
            <div className="form-group">
              <label>
                Type <span className="optional-text">(Optional)</span>
              </label>
              <select
                onChange={(e) =>
                  handleNestedInput("fixedMonthlyPrice", "type", e.target.value)
                }
                defaultValue=""
              >
                <option value="">Select Type</option>
                <option value="institute">Institute</option>
                <option value="instructor">Instructor</option>
              </select>
            </div>
            <div className="form-group">
              <label>
                Fixed Monthly Price{" "}
                <span className="optional-text">(Optional)</span>
              </label>
              <input
                type="number"
                onChange={(e) =>
                  handleNestedInput(
                    "fixedMonthlyPrice",
                    "price",
                    convertSafelyToNumber(e.target.value)
                  )
                }
                placeholder="Enter monthly price"
              />
            </div>
          </div>
          {formValidationErrors.fixedMonthlyPrice && (
            <p className="error" style={{ marginTop: "-50px" }}>
              {formValidationErrors.fixedMonthlyPrice}
            </p>
          )}

          {[
            "STEMGradingPrice",
            "NONSTEMGradingPrice",
            "questionBankGenPrice",
            "imageGenPrice",
          ].map((key) => (
            <React.Fragment key={key}>
              <label>
                {fieldLabels[key]}{" "}
                <span className="optional-text">(Optional)</span>
              </label>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    No Charge Qty{" "}
                    <span className="optional-text">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    onChange={(e) =>
                      handleNestedInput(
                        key,
                        "noChargeQuantity",
                        convertSafelyToNumber(e.target.value)
                      )
                    }
                    placeholder="Enter free usage quantity"
                  />
                </div>
                <div className="form-group">
                  <label>
                    Per Usage Price{" "}
                    <span className="optional-text">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    onChange={(e) =>
                      handleNestedInput(
                        key,
                        "perUsagePrice",
                        convertSafelyToNumber(e.target.value)
                      )
                    }
                    placeholder="Enter price per usage"
                  />
                </div>
              </div>
              {formValidationErrors[key] && (
                <p className="error" style={{ marginTop: "-50px" }}>
                  {formValidationErrors[key]}
                </p>
              )}
            </React.Fragment>
          ))}

          {serverValidationErrors.length > 0 &&
            serverValidationErrors.map((error, idx) => (
              <p key={idx} className="error" style={{ marginTop: "-50px" }}>
                {error}
              </p>
            ))}

          {!isSubmitting ? (
            <button type="submit" className="btn-submit">
              <i className="fa-solid fa-plus"></i> Submit Contract
            </button>
          ) : (
            <Loader fontSize="50px" />
          )}
        </form>
      </Modal>

      <Modal
        isOpen={isEditModalOpen && editingContract}
        onClose={handleCloseEditModal}
      >
        <h2>Edit Contract</h2>
        <form onSubmit={handleEditSubmit} className="admin-form">
          <div className="form-group">
            <label>
              Upload Contract Document (.pdf, .jpg, .jpeg, .png){" "}
              <span className="optional-text">(Optional)</span>
            </label>
            <input
              type="file"
              onChange={handleEditFileChange}
              accept=".pdf,.jpg,.jpeg,.png"
              placeholder="Upload new contract document"
            />
            {editingContract?.documentURL && !editingContractDocument && (
              <div className="current-file">
                <p>
                  Current file:
                  <a
                    href={editingContract?.documentURL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View PDF
                  </a>
                </p>
              </div>
            )}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>
                <i className="fa-solid fa-asterisk requiredIcon"></i> Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={editingContract?.startDate || ""}
                onChange={handleEditInput}
                required
                placeholder="Edit contract start date"
              />
            </div>
            <div className="form-group">
              <label>
                End Date <span className="optional-text">(Optional)</span>
              </label>
              <input
                type="date"
                name="endDate"
                value={editingContract?.endDate || ""}
                onChange={handleEditInput}
                placeholder="Edit contract end date"
              />
            </div>
          </div>
          <div className="form-group">
            <label>
              <i className="fa-solid fa-asterisk requiredIcon"></i> Currency
            </label>
            <input
              type="text"
              name="currency"
              value={editingContract?.currency || ""}
              onChange={handleEditInput}
              required
              placeholder="Edit currency (e.g., INR, USD)"
            />
          </div>
          <label>
            Fixed Monthly Price{" "}
            <span className="optional-text">(Optional)</span>
          </label>
          <div className="form-row">
            <div className="form-group">
              <label>
                Type <span className="optional-text">(Optional)</span>
              </label>
              <select
                value={editingContract?.fixedMonthlyPrice?.type || ""}
                onChange={(e) =>
                  handleEditNestedInput(
                    "fixedMonthlyPrice",
                    "type",
                    e.target.value
                  )
                }
              >
                <option value="">Select Type</option>
                <option value="institute">Institute</option>
                <option value="instructor">Instructor</option>
              </select>
            </div>
            <div className="form-group">
              <label>
                Fixed Monthly Price{" "}
                <span className="optional-text">(Optional)</span>
              </label>
              <input
                type="number"
                value={editingContract?.fixedMonthlyPrice?.price ?? ""}
                onChange={(e) =>
                  handleEditNestedInput(
                    "fixedMonthlyPrice",
                    "price",
                    convertSafelyToNumber(e.target.value)
                  )
                }
                placeholder="Edit monthly price"
              />
            </div>
          </div>
          {formValidationErrors.fixedMonthlyPrice && (
            <p className="error" style={{ marginTop: "-50px" }}>
              {formValidationErrors.fixedMonthlyPrice}
            </p>
          )}

          {[
            "STEMGradingPrice",
            "NONSTEMGradingPrice",
            "questionBankGenPrice",
            "imageGenPrice",
          ].map((key) => (
            <React.Fragment key={key}>
              <label>
                {fieldLabels[key]}{" "}
                <span className="optional-text">(Optional)</span>
              </label>
              <div className="form-row">
                <div className="form-group">
                  <label>
                    No Charge Qty{" "}
                    <span className="optional-text">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    value={
                      editingContract
                        ? editingContract[key]?.noChargeQuantity ?? ""
                        : ""
                    }
                    onChange={(e) =>
                      handleEditNestedInput(
                        key,
                        "noChargeQuantity",
                        convertSafelyToNumber(e.target.value)
                      )
                    }
                    placeholder="Edit free usage quantity"
                  />
                </div>
                <div className="form-group">
                  <label>
                    Per Usage Price{" "}
                    <span className="optional-text">(Optional)</span>
                  </label>
                  <input
                    type="number"
                    value={
                      editingContract
                        ? editingContract[key]?.perUsagePrice ?? ""
                        : ""
                    }
                    onChange={(e) =>
                      handleEditNestedInput(
                        key,
                        "perUsagePrice",
                        convertSafelyToNumber(e.target.value)
                      )
                    }
                    placeholder="Edit price per usage"
                  />
                </div>
              </div>
              {formValidationErrors[key] && (
                <p className="error" style={{ marginTop: "-50px" }}>
                  {formValidationErrors[key]}
                </p>
              )}
            </React.Fragment>
          ))}

          {serverValidationErrors.length > 0 &&
            serverValidationErrors.map((error, index) => (
              <p key={index} className="error" style={{ marginTop: "-50px" }}>
                {error}
              </p>
            ))}

          <div className="button-group">
            {!isSubmitting ? (
              <button
                type="submit"
                className="btn-submit"
                disabled={!isEditModalChanged}
              >
                <i className="fa-solid fa-floppy-disk"></i> Update Contract
              </button>
            ) : (
              <Loader fontSize="50px" />
            )}
          </div>
        </form>
      </Modal>
    </>
  );
};

export default ManageContract;
